import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    TextField,
    Button,
    Chip,
    Grid,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Switch,
    FormControlLabel,
    FormHelperText,
    CircularProgress
} from '@mui/material';
import { Save as SaveIcon, CheckCircle as CheckIcon, Cancel as XIcon, Link as LinkIcon } from '@mui/icons-material';
import styled from '@emotion/styled';
import { AdminPageContainer, StaticCard } from '../../../components/admin/sharedStyles';
import PageHeader from '../../../components/PageHeader';
import ConfigSubMenu from '../../../components/admin/ConfigSubMenu';
import { useDispatch } from 'react-redux';
import { showAlert } from '../../../redux/slices/alerts';
import { getConfigValue, saveConfig, testHubspotConnection, testIntuitConnection } from '../../../redux/slices/config';
import { getIntuitConnectionStatus, getInvoiceNumberPreview } from '../../../redux/ducks/invoices';
import { getApiUrl } from '../../../config';
import useUnsavedChanges from '../../../hooks/useUnsavedChanges';
import ConfirmDialog from '../../../components/admin/ConfirmDialog';
import { useBrandName } from '../../../utils/brandUtils';
import { formatDateTime } from '../../../utils/dateUtils';

const LoadingWrapper = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'center',
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4),
}));

const Section = styled(StaticCard)(({ theme }) => ({
    marginBottom: theme.spacing(3),
}));

const SectionDescription = styled(Typography)(({ theme }) => ({
    marginBottom: theme.spacing(2),
}));

const FooterActions = styled(Box)({
    display: 'flex',
    justifyContent: 'flex-end',
});

const GatewayBlock = styled(Box)(({ theme }) => ({
    marginTop: theme.spacing(2),
    paddingTop: theme.spacing(2),
    borderTop: `1px solid ${theme.palette.divider}`,
}));

const TestResultCaption = styled(Typography)(({ theme }) => ({
    display: 'block',
    marginTop: theme.spacing(1),
}));

const ConnectionStatusRow = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(1),
}));

const ConnectionMetaCaption = styled(Typography)(({ theme }) => ({
    display: 'block',
    marginTop: theme.spacing(0.5),
    color: theme.palette.text.secondary,
}));

const RedirectUriCallout = styled(Box)(({ theme }) => ({
    marginTop: theme.spacing(2),
    padding: theme.spacing(1.5),
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.action.hover,
    fontFamily: 'monospace',
    fontSize: theme.typography.size.smd,
    wordBreak: 'break-all',
}));

const RedirectUriCalloutLead = styled(Typography)(({ theme }) => ({
    fontFamily: theme.typography.fontFamily,
    marginBottom: theme.spacing(0.5),
}));

const BillingConfig = () => {
    const dispatch = useDispatch();
    const brandName = useBrandName();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [isDirty, setIsDirty] = useState(false);
    const [formData, setFormData] = useState({
        taxEnabled: false,
        taxIncluded: false,
        taxRate: 0,
        paymentTerms: 'Net 30',
        invoiceNumberPattern: 'INV-{YYYY}-####',
        invoiceNumberNext: '',
        overdueReminderDays: 7,
        paidChargePurgeDays: 90,
        paymentGateway: 'none',
        sendPolluxInvoice: false,
        sendIntuitInvoice: false,
        // Carried through because saving replaces the whole config object.
        intuitItemId: '',
        hubspotPortalId: '',
        hubspotEnabled: false,
    });
    const [numberPreview, setNumberPreview] = useState(null);
    const [testingHubspot, setTestingHubspot] = useState(false);
    const [hubspotTestResult, setHubspotTestResult] = useState(null);
    const [testingIntuit, setTestingIntuit] = useState(false);
    const [intuitTestResult, setIntuitTestResult] = useState(null);
    const [intuitStatus, setIntuitStatus] = useState(null);
    const [intuitStatusLoading, setIntuitStatusLoading] = useState(true);

    const loadIntuitStatus = () => {
        setIntuitStatusLoading(true);
        dispatch(getIntuitConnectionStatus((data) => {
            setIntuitStatus(data);
            setIntuitStatusLoading(false);
        }));
    };

    useEffect(() => {
        loadConfig();
        loadIntuitStatus();
        // Handle Intuit OAuth callback redirect query params on landing.
        const params = new URLSearchParams(window.location.search);
        if (params.get('intuitConnected') === 'true') {
            dispatch(showAlert({ severity: 'success', message: 'Intuit account connected successfully' }));
            params.delete('intuitConnected');
            const next = params.toString();
            window.history.replaceState({}, '', `${window.location.pathname}${next ? `?${next}` : ''}`);
        } else if (params.get('intuitError')) {
            dispatch(showAlert({ severity: 'error', message: `Intuit connection failed: ${params.get('intuitError')}` }));
            params.delete('intuitError');
            const next = params.toString();
            window.history.replaceState({}, '', `${window.location.pathname}${next ? `?${next}` : ''}`);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadConfig = () => {
        setLoading(true);
        dispatch(getConfigValue({
            key: 'billingConfig',
            value: null,
            callback: (data) => {
                if (data?.value) {
                    const val = data.value;
                    setFormData(prev => ({
                        ...prev,
                        taxEnabled: val.taxEnabled || false,
                        taxIncluded: val.taxIncluded || false,
                        taxRate: val.taxRate || 0,
                        paymentTerms: val.paymentTerms || 'Net 30',
                        invoiceNumberPattern: val.invoiceNumberPattern || 'INV-{YYYY}-####',
                        invoiceNumberNext: val.invoiceNumberNext ?? '',
                        overdueReminderDays: val.overdueReminderDays ?? 7,
                        paidChargePurgeDays: val.paidChargePurgeDays ?? 90,
                        paymentGateway: val.paymentGateway ?? (val.hubspotEnabled ? 'hubspot' : 'none'),
                        sendPolluxInvoice: val.sendPolluxInvoice === true,
                        sendIntuitInvoice: val.sendIntuitInvoice === true,
                        intuitItemId: val.intuitItemId || '',
                        hubspotPortalId: val.hubspotPortalId || '',
                        hubspotEnabled: val.hubspotEnabled || false,
                    }));
                }
                setLoading(false);
            }
        }));
    };

    useEffect(() => {
        const pattern = formData.invoiceNumberPattern;
        if (!pattern) {
            setNumberPreview(null);
            return undefined;
        }
        const timer = setTimeout(() => {
            dispatch(getInvoiceNumberPreview(pattern, (data) => setNumberPreview(data)));
        }, 400);
        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formData.invoiceNumberPattern, formData.invoiceNumberNext]);

    const { showPrompt, confirmLeave, cancelLeave, message: unsavedMessage } = useUnsavedChanges(isDirty);

    const handleChange = (e) => {
        setIsDirty(true);
        const { name, value, checked, type } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleTestHubspot = () => {
        setTestingHubspot(true);
        setHubspotTestResult(null);
        dispatch(testHubspotConnection({
            callback: (data) => {
                setTestingHubspot(false);
                setHubspotTestResult(data);
                if (data?.ok) {
                    dispatch(showAlert({ severity: 'success', message: `Connected to HubSpot portal ${data.portalId}` }));
                }
            }
        }));
    };

    const handleConnectIntuit = () => {
        window.location.href = `${getApiUrl()}/billing/invoices/intuit/oauthStart`;
    };

    const handleTestIntuit = () => {
        setTestingIntuit(true);
        setIntuitTestResult(null);
        dispatch(testIntuitConnection({
            callback: (data) => {
                setTestingIntuit(false);
                setIntuitTestResult(data);
                if (data?.ok) {
                    const where = data.realmId ? `realm ${data.realmId}` : (data.environment || 'sandbox');
                    dispatch(showAlert({ severity: 'success', message: `Connected to Intuit (${where})` }));
                }
            }
        }));
    };

    const handleSave = () => {
        setSaving(true);
        // Keep hubspotEnabled in sync with paymentGateway so the backend's
        // legacy hubspotService.isEnabled() check (cfg.hubspotEnabled === true)
        // mirrors the user-selected gateway exactly.
        const payload = {
            ...formData,
            invoiceNumberNext: formData.invoiceNumberNext === '' ? undefined : Number(formData.invoiceNumberNext),
            hubspotEnabled: formData.paymentGateway === 'hubspot'
        };
        dispatch(saveConfig({
            key: 'billingConfig',
            value: payload,
            callback: (data) => {
                setSaving(false);
                if (data?.status === 0) return;
                setIsDirty(false);
                dispatch(showAlert({ severity: 'success', message: 'Billing configuration saved successfully' }));
            }
        }));
    };

    return (
        <AdminPageContainer>
            <PageHeader
                title="Configuration"
                subtitle="Manage system configuration settings"
            />

            <ConfigSubMenu />

            {loading ? (
                <LoadingWrapper>
                    <CircularProgress />
                </LoadingWrapper>
            ) : (
                <>
                    <Section>
                        <Typography variant="h6" gutterBottom>
                            Tax Settings
                        </Typography>

                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            name="taxEnabled"
                                            checked={formData.taxEnabled}
                                            onChange={handleChange}
                                        />
                                    }
                                    label="Tax enabled"
                                />
                                <FormControlLabel
                                    control={
                                        <Switch
                                            name="taxIncluded"
                                            checked={formData.taxIncluded}
                                            onChange={handleChange}
                                        />
                                    }
                                    label="Tax included in price"
                                />
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label="Tax Rate (%)"
                                    name="taxRate"
                                    type="number"
                                    value={formData.taxRate}
                                    onChange={handleChange}
                                    inputProps={{ min: 0, max: 100, step: 0.01 }}
                                />
                            </Grid>
                        </Grid>
                    </Section>

                    <Section>
                        <Typography variant="h6" gutterBottom>
                            Invoice Numbering
                        </Typography>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                            How each new invoice number is built. Existing invoices keep the numbers they were given.
                        </Typography>
                        <Grid container spacing={3}>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label="Invoice number pattern"
                                    name="invoiceNumberPattern"
                                    value={formData.invoiceNumberPattern}
                                    onChange={handleChange}
                                    error={numberPreview ? numberPreview.valid === false : false}
                                    helperText={
                                        numberPreview && numberPreview.valid === false
                                            ? numberPreview.message
                                            : "Use # for the number — four of them gives 0099. {YYYY} is the year, {YY} the short year, {MM} the month."
                                    }
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label="Next invoice number"
                                    name="invoiceNumberNext"
                                    type="number"
                                    value={formData.invoiceNumberNext}
                                    onChange={handleChange}
                                    inputProps={{ min: 1, step: 1 }}
                                    helperText="Leave empty to carry on counting. Set it to start from a number, which is useful when moving over from another system. It only ever moves numbering forward."
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <Typography variant="body2" color="text.secondary">
                                    {numberPreview?.valid
                                        ? `The next invoice would be numbered ${numberPreview.preview}`
                                        : "The next invoice number will appear here once the pattern is valid."}
                                </Typography>
                            </Grid>
                        </Grid>
                    </Section>

                    <Section>
                        <Typography variant="h6" gutterBottom>
                            Invoice Settings
                        </Typography>

                        <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth>
                                    <InputLabel>Default Payment Terms</InputLabel>
                                    <Select
                                        name="paymentTerms"
                                        value={formData.paymentTerms}
                                        onChange={handleChange}
                                        label="Default Payment Terms"
                                    >
                                        <MenuItem value="Due on Receipt">Due on Receipt</MenuItem>
                                        <MenuItem value="Net 15">Net 15</MenuItem>
                                        <MenuItem value="Net 21">Net 21</MenuItem>
                                        <MenuItem value="Net 30">Net 30</MenuItem>
                                        <MenuItem value="Net 60">Net 60</MenuItem>
                                        <MenuItem value="Net 90">Net 90</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                        </Grid>
                    </Section>

                    <Section>
                        <Typography variant="h6" gutterBottom>
                            Maintenance Policy
                        </Typography>
                        <SectionDescription variant="body2" color="text.secondary">
                            Cadence and retention windows used by the daily maintenance job.
                        </SectionDescription>

                        <Grid container spacing={2}>
                            <Grid item xs={12} md={4}>
                                <TextField
                                    fullWidth
                                    label="Overdue reminder cadence (days)"
                                    name="overdueReminderDays"
                                    type="number"
                                    value={formData.overdueReminderDays}
                                    onChange={handleChange}
                                    inputProps={{ min: 1, step: 1 }}
                                    helperText="Days between overdue-invoice reminder emails"
                                />
                            </Grid>

                            <Grid item xs={12} md={4}>
                                <TextField
                                    fullWidth
                                    label="Paid charge retention (days)"
                                    name="paidChargePurgeDays"
                                    type="number"
                                    value={formData.paidChargePurgeDays}
                                    onChange={handleChange}
                                    inputProps={{ min: 1, step: 1 }}
                                    helperText="Delete one-off Paid charges older than this"
                                />
                            </Grid>
                        </Grid>
                    </Section>

                    <Section>
                        <Typography variant="h6" gutterBottom>
                            Payment Gateway
                        </Typography>
                        <SectionDescription variant="body2" color="text.secondary">
                            Outbound invoices will be pushed to the selected gateway when a Draft is sent. Once an invoice is synced,
                            it is locked from local edits here — void it from the remote gateway and reissue to make changes.
                        </SectionDescription>

                        <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth>
                                    <InputLabel id="paymentGateway-label">Active Payment Gateway</InputLabel>
                                    <Select
                                        labelId="paymentGateway-label"
                                        name="paymentGateway"
                                        value={formData.paymentGateway}
                                        onChange={handleChange}
                                        label="Active Payment Gateway"
                                    >
                                        <MenuItem value="none">None — invoices are sent and paid locally</MenuItem>
                                        <MenuItem value="hubspot">HubSpot — push to HubSpot hosted payment page</MenuItem>
                                        <MenuItem value="intuit">Intuit Payments — push to Intuit hosted payment link</MenuItem>
                                    </Select>
                                    <FormHelperText>
                                        Only one gateway is active at a time. Existing invoices stay associated with whichever
                                        gateway pushed them, regardless of this setting.
                                    </FormHelperText>
                                </FormControl>
                            </Grid>
                        </Grid>

                        <GatewayBlock>
                            <Typography variant="subtitle2" gutterBottom>
                                Who emails the invoice
                            </Typography>
                            <SectionDescription variant="body2" color="text.secondary">
                                Choose which system delivers the invoice to the client when a Draft is sent. Turning both on means
                                the client receives two emails; turning both off sends nothing and leaves delivery to you.
                            </SectionDescription>
                            <FormControlLabel
                                control={
                                    <Switch
                                        name="sendPolluxInvoice"
                                        checked={formData.sendPolluxInvoice}
                                        onChange={handleChange}
                                    />
                                }
                                label="Send the Pollux invoice"
                            />
                            <FormHelperText sx={{ mt: -1, mb: 1 }}>
                                Emails the billing contact and the primary contact, with your PDF attached and a pay button when a
                                payment link exists.
                            </FormHelperText>
                            <FormControlLabel
                                control={
                                    <Switch
                                        name="sendIntuitInvoice"
                                        checked={formData.sendIntuitInvoice}
                                        onChange={handleChange}
                                        disabled={formData.paymentGateway !== "intuit"}
                                    />
                                }
                                label="Send the Intuit invoice"
                            />
                            <FormHelperText sx={{ mt: -1 }}>
                                {formData.paymentGateway === "intuit"
                                    ? "QuickBooks emails the billing contact directly, using its own template and PDF, and records the invoice as sent in your books."
                                    : "Available when Intuit is the active payment gateway."}
                            </FormHelperText>
                        </GatewayBlock>

                        {formData.paymentGateway === 'none' && (
                            <GatewayBlock>
                                <Typography variant="body2" color="text.secondary">
                                    No external gateway configured. Invoices are sent by email with a PDF attachment and recorded as
                                    paid manually here.
                                </Typography>
                            </GatewayBlock>
                        )}

                        {formData.paymentGateway === 'hubspot' && (
                            <GatewayBlock>
                                <SectionDescription variant="body2" color="text.secondary">
                                    HubSpot creates a hosted payment page for every Open invoice. The Private App access token is read
                                    from the API container environment (<code>HUBSPOT_API_TOKEN</code>); webhook deliveries are
                                    verified against <code>HUBSPOT_CLIENT_SECRET</code>.
                                </SectionDescription>

                                <Grid container spacing={2} alignItems="center">
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            fullWidth
                                            label="HubSpot Portal ID"
                                            name="hubspotPortalId"
                                            value={formData.hubspotPortalId}
                                            onChange={handleChange}
                                            helperText="For reference / display only — auth uses the env token."
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <Button
                                            variant="outlined"
                                            onClick={handleTestHubspot}
                                            disabled={testingHubspot}
                                            startIcon={
                                                hubspotTestResult == null
                                                    ? null
                                                    : (hubspotTestResult.ok ? <CheckIcon color="success" /> : <XIcon color="error" />)
                                            }
                                        >
                                            {testingHubspot ? 'Testing HubSpot…' : 'Test HubSpot Connection'}
                                        </Button>
                                        {hubspotTestResult && (
                                            <TestResultCaption
                                                variant="caption"
                                                color={hubspotTestResult.ok ? 'success.main' : 'error.main'}
                                            >
                                                {hubspotTestResult.ok
                                                    ? `Connected — portal ${hubspotTestResult.portalId}`
                                                    : `Failed: ${hubspotTestResult.message || 'Unknown error'}`}
                                            </TestResultCaption>
                                        )}
                                    </Grid>
                                </Grid>
                            </GatewayBlock>
                        )}

                        {formData.paymentGateway === 'intuit' && (
                            <GatewayBlock>
                                <SectionDescription variant="body2" color="text.secondary">
                                    Intuit Payments creates a hosted payment link for every Open invoice. Configure the Intuit app
                                    credentials in the API container environment: <code>INTUIT_CLIENT_ID</code>,{' '}
                                    <code>INTUIT_CLIENT_SECRET</code>, <code>INTUIT_ENVIRONMENT</code> (<code>sandbox</code> or{' '}
                                    <code>production</code>, defaults to <code>sandbox</code>), and{' '}
                                    <code>INTUIT_WEBHOOK_VERIFIER_TOKEN</code>. The redirect URL, merchant Realm ID, and refresh token
                                    are derived or captured in-app via the Connect button below — no token rotation work for the operator.
                                </SectionDescription>

                                {intuitStatusLoading ? (
                                    <Typography variant="body2" color="text.secondary">Checking Intuit connection status…</Typography>
                                ) : intuitStatus?.connected ? (
                                    <>
                                        <ConnectionStatusRow>
                                            <Chip size="small" color="success" variant="outlined" label="Connected to Intuit" />
                                            <Button
                                                variant="outlined"
                                                size="small"
                                                startIcon={<LinkIcon />}
                                                onClick={handleConnectIntuit}
                                            >
                                                Reconnect to Intuit
                                            </Button>
                                            <Button
                                                variant="outlined"
                                                size="small"
                                                onClick={handleTestIntuit}
                                                disabled={testingIntuit}
                                                startIcon={
                                                    intuitTestResult == null
                                                        ? null
                                                        : (intuitTestResult.ok ? <CheckIcon color="success" /> : <XIcon color="error" />)
                                                }
                                            >
                                                {testingIntuit ? 'Testing Intuit…' : 'Test Intuit Connection'}
                                            </Button>
                                        </ConnectionStatusRow>
                                        <ConnectionMetaCaption variant="caption">
                                            Merchant Realm: <strong>{intuitStatus.realmId || '(none)'}</strong>
                                            {intuitStatus.environment ? ` · Environment: ${intuitStatus.environment}` : ''}
                                            {intuitStatus.connectedByEmail ? ` · Connected by ${intuitStatus.connectedByEmail}` : ''}
                                            {intuitStatus.connectedAt ? ` on ${formatDateTime(intuitStatus.connectedAt)}` : ''}
                                            {intuitStatus.rotatedAt ? ` · Last rotated ${formatDateTime(intuitStatus.rotatedAt)}` : ''}
                                        </ConnectionMetaCaption>
                                        {intuitTestResult && (
                                            <TestResultCaption
                                                variant="caption"
                                                color={intuitTestResult.ok ? 'success.main' : 'error.main'}
                                            >
                                                {intuitTestResult.ok
                                                    ? `Connected — ${intuitTestResult.realmId ? `realm ${intuitTestResult.realmId}` : (intuitTestResult.environment || 'sandbox')}`
                                                    : `Failed: ${intuitTestResult.message || 'Unknown error'}`}
                                            </TestResultCaption>
                                        )}
                                    </>
                                ) : (
                                    <ConnectionStatusRow>
                                        <Chip size="small" color="default" variant="outlined" label="Not connected" />
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            startIcon={<LinkIcon />}
                                            onClick={handleConnectIntuit}
                                        >
                                            Connect to Intuit
                                        </Button>
                                        <Typography variant="caption" color="text.secondary">
                                            You'll be redirected to Intuit to authorize {brandName}, then returned here.
                                        </Typography>
                                    </ConnectionStatusRow>
                                )}

                                <RedirectUriCallout>
                                    <RedirectUriCalloutLead variant="caption" component="div" color="text.secondary">
                                        {intuitStatus?.redirectUri
                                            ? <><strong>Setup (one-time):</strong> add this exact URL to your Intuit app's <em>Allowed Redirect URIs</em> in the Intuit developer portal (My Apps → your app → Keys &amp; OAuth):</>
                                            : <><strong>Setup (one-time):</strong> could not derive the public origin from the request — check the reverse-proxy <code>X-Forwarded-*</code> headers.</>}
                                    </RedirectUriCalloutLead>
                                    {intuitStatus?.redirectUri || null}
                                </RedirectUriCallout>
                            </GatewayBlock>
                        )}
                    </Section>

                    <FooterActions>
                        <Button
                            variant="contained"
                            color="primary"
                            startIcon={<SaveIcon />}
                            onClick={handleSave}
                            disabled={saving}
                        >
                            {saving ? 'Saving Billing Settings…' : 'Save Billing Settings'}
                        </Button>
                    </FooterActions>
                </>
            )}
            <ConfirmDialog
                open={showPrompt}
                onClose={cancelLeave}
                onConfirm={confirmLeave}
                title="Unsaved Changes"
                message={unsavedMessage}
                confirmLabel="Leave"
                confirmColor="warning"
            />
        </AdminPageContainer>
    );
};

export default BillingConfig;
